/* Copyright 2013 - 2024 Waiterio LLC */
/** @jsx jsx */
import React, { Suspense, useState, useEffect, useRef, useMemo } from 'react'
import { css, jsx } from '@emotion/react'
import { Link } from 'react-router-dom'
import { useContext } from '@monorepo/context/index.js'
import ImageDialog from '@stiloso/components/ImageDialog.js'
import useAddMessageSubscription from '@globo/react/useAddMessageSubscription.js'
import useConversationMutation from '@globo/react/useConversationMutation.js'
import useConversations from '@globo/react/useConversations.js'
import useCustomers from '@globo/react/useCustomers.js'
import useCustomerMutation from '@globo/react/useCustomerMutation.js'
import useMessages from '@globo/react/useMessages.js'
import useAddMessageMutation from '@globo/react/useAddMessageMutation.js'
import usePresenceSubscription from '@globo/react/usePresenceSubscription.js'
import useWebsite from '@globo/react/useWebsite.js'
import { useTranslation } from '@multilocale/react/index.js'
import uuid from '@monorepo/shared/uuid.js'
import IconAdd from '@stiloso/icons/IconAdd.js'
import readFile from '@monorepo/shared/readFile.js'
import ImageGraceful from '@stiloso/components/ImageGraceful.js'
import IconBack from '@stiloso/icons/IconBack.js'
import IconClose from '@stiloso/icons/IconClose.js'
import spinner from '@stiloso/styles/spinner.js'
import Spinner from '@stiloso/components/Spinner.js'
import clickable from '@stiloso/styles/clickable.js'
import IconError from '@stiloso/icons/IconError.js'
import colorRed from '@stiloso/styles/red.js'
import useDocuments from '../useDocuments.js'

export const paths = ['/messages']

const ContentEditable = props => {
  const contentEditableRef = useRef(null)

  useEffect(() => {
    if (contentEditableRef.current.textContent !== props.value) {
      contentEditableRef.current.textContent = props.value
    }
  })

  return (
    <div
      contentEditable="true"
      ref={contentEditableRef}
      name="messageInput"
      placeholder={props.placeholder}
      disabled={props.disabled}
      onKeyDown={props.onKeyDown}
      onInput={event => {
        props.onChange(event.target.textContent)
      }}
      autoCapitalize="off"
      autoComplete="off"
      autoCorrect="off"
      autoFocus
      css={{
        lineHeight: '26px',
        width: '100%',
        overflowY: 'hidden',
        padding: 16,
        outline: 0,
        whiteSpace: 'pre-wrap',
        ':empty::before': {
          content: `"${props.placeholder} "`,
          color: '#ABABAB',
        },
      }}
    />
  )
}

const Messages = () => {
  const [context, produce] = useContext()
  let {
    customerId,
    deviceType,
    metadata,
    version,
    versionNative,
    userEmail,
    userAvatarUrl,
    userId,
    userNickname,
    userPhone,
    websiteId,
  } = context

  const { data: website } = useWebsite(websiteId)
  const organizationId = website?.organizationId

  const { data: customers } = useCustomers(
    JSON.parse(JSON.stringify({ websiteId, _id: customerId, userId })),
    {
      enabled: !!(customerId || (websiteId && userId)),
    },
  )
  let customer = customers?.[0]
  customerId = customer?._id
  const { t } = useTranslation()
  const messagesContainerRef = useRef(null)
  const conversationsFilter = {
    organizationId,
    websiteId,
    customerId: customerId || 'new',
  }

  let { data: conversations } = useConversations(conversationsFilter, {
    enabled: !!(websiteId && customer?._id),
  })

  let currentConversationId = conversations?.[0]?._id || 'new'

  const messagesFilter = {
    organizationId,
    conversationId: currentConversationId,
    sortField: 'creationTime',
    sortDirection: 'DESC',
  }

  usePresenceSubscription({ websiteId: website?._id, customerId })
  useAddMessageSubscription(websiteId)

  let { data: messages } = useMessages(messagesFilter, {
    enabled: !!currentConversationId,
  })
  messages = messages || []
  messages = messages.filter(message => !message.hasNote)
  messages = messages.map(message => {
    message ||= {}

    return {
      ...message,
      content: message.content?.replaceAll(
        /(http|ftp|https):\/\/([\w_-]+(?:(?:\.[\w_-]+)+))([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])/g,
        url => `<a href="${url}" target="_blank">${url}</a>`,
      ),
    }
  })

  const concatenatedMessages = useMemo(
    () => [
      ...messages,
      {
        _id: uuid(),
        content: t('How can we help you?'),
      },
    ],
    [messages, t],
  )

  let [newMessageContent, setNewMessageContent] = useState('')
  let [isUploadingImage, setIsUploadingImage] = useState(false)
  let { mutate: mutateConversation } = useConversationMutation()
  let { mutate: mutateCustomer } = useCustomerMutation()
  let {
    mutate: addMessage,
    isError: addMessageError,
    variables: addMessageVariables,
  } = useAddMessageMutation(messagesFilter)

  let currentConversation = conversations?.find(
    conversation => conversation._id === currentConversationId,
  )

  const onSendMessage = async file => {
    let creationTime = new Date().toISOString()
    let lastEditTime = creationTime
    let promises = []

    if (!customer) {
      customer = {
        _id: uuid(),
        creationTime,
        lastEditTime,
        email: userEmail,
        nickname: userNickname,
        avatarUrl: userAvatarUrl,
        phone: userPhone,
        organizationId,
        websiteId,
        metadata,
        userId,
      }

      promises.push(mutateCustomer(customer))
    }

    if (!currentConversation) {
      currentConversationId = uuid()
      currentConversation = {
        _id: currentConversationId,
        creationTime,
        organizationId,
        customerId: customer?._id,
        customerAvatarUrl: customer?.avatarUrl,
        customerNickname:
          customer?.nickname ||
          customer?.email ||
          customer?.phone ||
          customer?._id,
        websiteId,
      }
    }

    currentConversation.lastEditTime = lastEditTime
    currentConversation.lastMessage = file
      ? file.name
      : newMessageContent.substring(0, 100)

    if (currentConversation.resolved) {
      currentConversation.resolved = false
    }

    promises.push(mutateConversation(currentConversation))

    const newMessage = {
      _id: uuid(),
      creationTime,
      lastEditTime,
      conversationId: currentConversation._id,
      organizationId: currentConversation.organizationId,
      websiteId: currentConversation.websiteId,
      content: file ? file.name : newMessageContent,
      from: 'customer',
      customerId: currentConversation.customerId,
      type: file ? 'file' : 'text',
      fileType: file ? file.extension : null,
      deviceType,
      userAgent: navigator.userAgent,
      version,
      versionNative,
    }

    promises.push(
      addMessage(
        { message: newMessage, file: file?.data },
        {
          onSuccess: () => setIsUploadingImage(false),
          onError: () => setIsUploadingImage(false),
        },
      ),
    )

    await Promise.all(promises)

    console.log('HERE')
    setNewMessageContent('')

    produce(draft => {
      draft.customerId = customer._id
    })
  }

  const handleKeyDown = event => {
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      newMessageContent.trim().length > 0
    ) {
      event.preventDefault()
      onSendMessage()
    }
  }

  const onClickFileUpload = () => {
    document.getElementById('uploadFileInput').click()
  }

  const uploadFile = async event => {
    let file = event.target.files[0]
    event.target.value = null
    let extension
    if (file.name && file.name.indexOf('.' > -1)) {
      const splits = file.name.split('.')
      extension = splits[splits.length - 1]
    } else if (file.type && file.type.indexOf('/') > -1) {
      const splits = file.type.split('/')
      extension = splits[splits.length - 1]
    }

    if (!['jpg', 'jpeg', 'png', 'webp'].includes(extension)) {
      throw new Error('Image should be in .jpg or .png or .webp format')
    }

    const data = await readFile(file)

    file = {
      data,
      extension,
      name: file.name,
    }

    setIsUploadingImage(true)
    onSendMessage(file)
  }

  useEffect(() => {
    const container = messagesContainerRef.current

    const newMessage = messages[messages.length - 1]
    const threshold = -400

    const isAtBottom = container.scrollTop >= threshold
    const isFromCustomer = newMessage?.from === 'customer'

    if (isAtBottom || isFromCustomer) {
      container.scrollTop = 0
    }
  }, [messages])

  return (
    <>
      <div
        ref={messagesContainerRef}
        css={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column-reverse',
          padding: 16,
          overflowY: 'scroll',
          overflowX: 'hidden',
        }}
      >
        {addMessageError && (
          <div
            onClick={() =>
              addMessage({
                message: addMessageVariables.message,
                file: addMessageVariables.file,
              })
            }
            css={[
              {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 4,
                padding: 8,
              },
              clickable,
            ]}
          >
            <IconError
              css={{ width: 28, height: 28, fill: colorRed, marginRight: 4 }}
            />
            <span>{t('Retry')}</span>
          </div>
        )}
        {addMessageError && (
          <MessageComponent
            addMessage={addMessage}
            addMessageError={addMessageError}
            addMessageVariables={addMessageVariables}
            {...addMessageVariables.message}
          />
        )}
        {concatenatedMessages.map(message => (
          <MessageComponent key={message._id} {...message} />
        ))}
      </div>
      <div
        css={{
          display: 'flex',
          backgroundColor: 'white',
          borderTop: '2px solid var(--color-background)',
        }}
      >
        <ContentEditable
          placeholder={t('Send a message...')}
          value={newMessageContent}
          disabled={isUploadingImage}
          onKeyDown={handleKeyDown}
          onChange={setNewMessageContent}
        />
        {isUploadingImage && (
          <div
            css={[
              spinner,
              {
                borderLeftColor: 'var(--color-primary)',
                padding: 12,
                margin: 16,
              },
            ]}
          />
        )}

        {!isUploadingImage && (
          <div
            onClick={onClickFileUpload}
            css={[
              {
                margin: '0px auto',
                cursor: 'pointer',
                padding: 16,
                maxHeight: 60,
              },
              clickable,
            ]}
          >
            <IconAdd
              css={{
                width: 24,
                height: 24,
              }}
            />
          </div>
        )}

        <input
          id="uploadFileInput"
          type="file"
          accept="image/*"
          capture
          css={{ display: 'none' }}
          tabIndex="-1"
          onChange={uploadFile}
          disabled={isUploadingImage}
        />
      </div>
    </>
  )
}

const MessageComponent = ({ from, fileUrl, content, addMessageError }) => {
  const [showImageDialog, setShowImageDialog] = useState(null)

  return (
    <>
      <div
        css={[
          {
            position: 'relative',
            background: from === 'customer' ? '#EFFFDE' : '#F6F6F6',
            color: 'var(--color-base)',
            margin: 8,
            borderRadius: 8,
            padding: 16,
            marginLeft: from === 'customer' ? 32 : null,
            marginRight: from === 'customer' ? null : 32,
            alignSelf: from === 'customer' ? 'flex-end' : 'flex-start',
            wordBreak: 'break-word',
            whiteSpace: 'pre-line',
          },
          addMessageError && { opacity: 0.5 },
        ]}
      >
        {fileUrl && (
          <ImageGraceful
            onClick={() => setShowImageDialog(fileUrl)}
            css={{
              minWidth: 280,
              maxWidth: 600,
              width: '100%',
            }}
            retry={{
              count: 10,
              delay: 1,
              accumulate: false,
            }}
            src={fileUrl}
          />
        )}

        {!fileUrl && <span dangerouslySetInnerHTML={{ __html: content }} />}
      </div>
      {showImageDialog && (
        <ImageDialog
          src={showImageDialog}
          onClose={() => setShowImageDialog(null)}
        />
      )}
    </>
  )
}

const Header = () => {
  const { t } = useTranslation()
  let { data: documents } = useDocuments()

  let showDocuments = documents?.length > 0

  const closeWidget = () => {
    window?.parent?.postMessage({ type: 'closeChat' }, '*')
  }

  return (
    <div css={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <div
        css={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={closeWidget}
          css={{
            background: 'transparent',
            border: 'none',
            padding: 16,
            cursor: 'pointer',
            ':hover, :focus-visible': {
              background: 'var(--color-hover)',
            },
            ':active': {
              background: 'var(--color-active)',
            },
            visibility: 'hidden',
          }}
        >
          <IconBack css={{ width: 24, height: 24 }} />
        </button>
        <Link
          css={{
            color: 'white',
            flexGrow: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            alignSelf: 'center',
            cursor: showDocuments ? 'pointer' : 'default',
            height: 32,
            borderRadius: 24,
            background: showDocuments ? 'var(--color-primary-dark)' : 'initial',
            ':hover, :focus-visible': {
              background: showDocuments ? 'var(--color-hover)' : 'initial',
            },
            ':active': {
              background: showDocuments ? 'var(--color-active)' : 'initial',
            },
          }}
          to="/messages"
        >
          {t('Chat')}
        </Link>
        <Link
          css={{
            color: 'white',
            flexGrow: 1,
            display: showDocuments ? 'flex' : 'none',
            justifyContent: 'center',
            alignItems: 'center',
            alignSelf: 'center',
            cursor: 'pointer',
            height: 32,
            borderRadius: 24,

            ':hover, :focus-visible': {
              background: 'var(--color-hover)',
            },
            ':active': {
              background: 'var(--color-active)',
            },
          }}
          to="/documents"
        >
          {t('Docs')}
        </Link>
        <button
          type="button"
          aria-label="Close"
          onClick={closeWidget}
          css={{
            background: 'transparent',
            border: 'none',
            padding: 16,
            cursor: 'pointer',
            ':hover, :focus-visible': {
              background: 'var(--color-hover)',
            },
            ':active': {
              background: 'var(--color-active)',
            },
          }}
        >
          <IconClose css={{ width: 24, height: 24 }} />
        </button>
      </div>
    </div>
  )
}

const MessagesPageWrapper = () => (
  <div
    css={{
      background: 'white',
      borderRadius: 16,
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}
  >
    <div
      css={{
        display: 'flex',
        background: 'var(--color-primary)',
        color: 'white',
        fill: 'white',
        width: '100%',
        position: 'sticky',
        top: 0,
      }}
    >
      <Suspense>
        <Header />
      </Suspense>
    </div>
    <Suspense fallback={<Spinner />}>
      <Messages />
    </Suspense>
  </div>
)

export default MessagesPageWrapper
